/**
 * Falling-egg bonuses and their timing. Weights are relative; the picker
 * normalises them, so the numbers below read as percentages and sum to 100.
 *
 * Two boost slots run independently: production boosts (Rush, Frenzy) and
 * click boosts (the hands). Within a slot a stronger or equal boost replaces
 * the running one and a weaker one is a whiff. Jackpots pay at once.
 */

import type { BoostKind } from '../game/state';

export type BonusEffect =
  /** All production times `mult` for `seconds`. */
  | { kind: 'boost'; boost: BoostKind; mult: number; seconds: number }
  /** Each click times `mult` for `seconds`. */
  | { kind: 'clickBoost'; boost: BoostKind; mult: number; seconds: number }
  | {
      kind: 'jackpot';
      /** Lump sum equals this many minutes of current EPS... */
      minutesOfEps: number;
      /** ...capped at this fraction of eggs banked... */
      capFractionOfBank: number;
      /** ...but never less than this. */
      floor: number;
    };

export interface BonusDef {
  id: string;
  name: string;
  description: string;
  weight: number;
  effect: BonusEffect;
}

export const BONUSES: readonly BonusDef[] = [
  {
    id: 'rush',
    name: 'Egg Rush',
    description: 'All production ×2 for 60 seconds.',
    weight: 49.9,
    effect: { kind: 'boost', boost: 'rush', mult: 2, seconds: 60 },
  },
  {
    id: 'frenzy',
    name: 'Egg Frenzy',
    description: 'All production ×5 for 120 seconds.',
    weight: 14,
    effect: { kind: 'boost', boost: 'frenzy', mult: 5, seconds: 120 },
  },
  {
    id: 'quick-hands',
    name: 'Quick Hands',
    description: 'Every click ×20 for 60 seconds.',
    weight: 4.5,
    effect: { kind: 'clickBoost', boost: 'quick-hands', mult: 20, seconds: 60 },
  },
  {
    id: 'golden-touch',
    name: 'Golden Touch',
    description: 'Every click ×100 for 30 seconds.',
    weight: 1,
    effect: { kind: 'clickBoost', boost: 'golden-touch', mult: 100, seconds: 30 },
  },
  {
    id: 'thunderclap',
    name: 'Thunderclap',
    description: 'Every click ×1000 for 15 seconds.',
    weight: 0.1,
    effect: { kind: 'clickBoost', boost: 'thunderclap', mult: 1000, seconds: 15 },
  },
  {
    id: 'jackpot',
    name: 'Egg Jackpot',
    description: 'A lump sum of eggs, right now.',
    weight: 30,
    effect: { kind: 'jackpot', minutesOfEps: 15, capFractionOfBank: 0.15, floor: 20 },
  },
  {
    id: 'motherlode',
    name: 'Egg Motherlode',
    description: 'A huge lump sum of eggs, right now.',
    weight: 0.5,
    effect: { kind: 'jackpot', minutesOfEps: 60, capFractionOfBank: 1, floor: 2000 },
  },
];

/** Spawn interval bounds, in seconds of active (visible-tab) play. */
export const SPAWN_MIN_SECONDS = 3 * 60;
export const SPAWN_MAX_SECONDS = 7 * 60;

/** How long a falling egg is on screen and catchable. */
export const FALL_SECONDS = 13;
